import React, { useEffect, useState } from 'react';
import { branchService } from '../services/api';
import { toast } from 'react-toastify';
import Table from '../components/Table';
import Button from '../components/Button';
import Modal from '../components/Modal';
import Input from '../components/Input';
import { FiPlus, FiEdit, FiTrash2 } from 'react-icons/fi';

const Branches = () => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentBranch, setCurrentBranch] = useState(null);
  const [formData, setFormData] = useState({
    branchName: '',
    branchCode: '',
    branchEmail: '',
    branchPhone: '',
    managerName: '',
    managerEmail: '',
    managerPassword: '',
    address: {
      street: '',
      city: '',
      state: '',
      country: '',
      zipCode: '',
    },
    settings: {
      currency: 'USD',
      taxRate: 0,
      receiptPrefix: 'INV',
    },
  });

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await branchService.getAll();
      setBranches(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch branches');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await branchService.update(currentBranch._id, formData);
        toast.success('Branch updated successfully');
      } else {
        await branchService.create(formData);
        toast.success('Branch created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchBranches();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleEdit = (branch) => {
    setCurrentBranch(branch);
    setFormData({
      branchName: branch.branchName,
      branchCode: branch.branchCode,
      branchEmail: branch.branchEmail || '',
      branchPhone: branch.branchPhone || '',
      address: branch.address || {
        street: '',
        city: '',
        state: '',
        country: '',
        zipCode: '',
      },
      settings: branch.settings || {
        currency: 'USD',
        taxRate: 0,
        receiptPrefix: 'INV',
      },
    });
    setEditMode(true);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this branch?')) {
      try {
        await branchService.delete(id);
        toast.success('Branch deleted successfully');
        fetchBranches();
      } catch (error) {
        toast.error('Failed to delete branch');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      branchName: '',
      branchCode: '',
      branchEmail: '',
      branchPhone: '',
      managerName: '',
      managerEmail: '',
      managerPassword: '',
      address: {
        street: '',
        city: '',
        state: '',
        country: '',
        zipCode: '',
      },
      settings: {
        currency: 'USD',
        taxRate: 0,
        receiptPrefix: 'INV',
      },
    });
    setEditMode(false);
    setCurrentBranch(null);
  };

  const columns = [
    { header: 'Branch Name', accessor: 'branchName' },
    { header: 'Code', accessor: 'branchCode' },
    { header: 'Email', accessor: 'branchEmail' },
    { header: 'City', render: (row) => row.address?.city || 'N/A' },
    { header: 'Products', render: (row) => row.stats?.totalProducts || 0 },
    {
      header: 'Status',
      render: (row) => (
        <span
          className={`px-2 py-1 text-xs rounded-full ${
            row.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {row.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      header: 'Actions',
      render: (row) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleEdit(row)}
            className="text-purple-600 hover:text-purple-800"
          >
            <FiEdit size={18} />
          </button>
          <button
            onClick={() => handleDelete(row._id)}
            className="text-red-600 hover:text-red-800"
          >
            <FiTrash2 size={18} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Branches</h1>
        <Button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
        >
          <FiPlus className="inline mr-2" /> Add Branch
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      ) : (
        <Table columns={columns} data={branches} />
      )}

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          resetForm();
        }}
        title={editMode ? 'Edit Branch' : 'Add New Branch'}
        size="lg"
      >
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Branch Name"
              name="branchName"
              value={formData.branchName}
              onChange={(e) => setFormData({ ...formData, branchName: e.target.value })}
              required
            />
            <Input
              label="Branch Code"
              name="branchCode"
              value={formData.branchCode}
              onChange={(e) =>
                setFormData({ ...formData, branchCode: e.target.value.toUpperCase() })
              }
              required
              disabled={editMode}
            />
            <Input
              label="Branch Email"
              type="email"
              name="branchEmail"
              value={formData.branchEmail}
              onChange={(e) => setFormData({ ...formData, branchEmail: e.target.value })}
            />
            <Input
              label="Branch Phone"
              name="branchPhone"
              value={formData.branchPhone}
              onChange={(e) => setFormData({ ...formData, branchPhone: e.target.value })}
            />
          </div>

          {!editMode && (
            <>
              <h3 className="text-lg font-semibold mt-6 mb-4">Branch Manager Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Manager Name"
                  name="managerName"
                  value={formData.managerName}
                  onChange={(e) => setFormData({ ...formData, managerName: e.target.value })}
                  required
                />
                <Input
                  label="Manager Email"
                  type="email"
                  name="managerEmail"
                  value={formData.managerEmail}
                  onChange={(e) => setFormData({ ...formData, managerEmail: e.target.value })}
                  required
                />
                <Input
                  label="Manager Password"
                  type="password"
                  name="managerPassword"
                  value={formData.managerPassword}
                  onChange={(e) =>
                    setFormData({ ...formData, managerPassword: e.target.value })
                  }
                  required
                />
              </div>
            </>
          )}

          <h3 className="text-lg font-semibold mt-6 mb-4">Address</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Street"
              value={formData.address.street}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  address: { ...formData.address, street: e.target.value },
                })
              }
            />
            <Input
              label="City"
              value={formData.address.city}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  address: { ...formData.address, city: e.target.value },
                })
              }
            />
            <Input
              label="State"
              value={formData.address.state}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  address: { ...formData.address, state: e.target.value },
                })
              }
            />
            <Input
              label="Country"
              value={formData.address.country}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  address: { ...formData.address, country: e.target.value },
                })
              }
            />
            <Input
              label="Zip Code"
              value={formData.address.zipCode}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  address: { ...formData.address, zipCode: e.target.value },
                })
              }
            />
          </div>

          <h3 className="text-lg font-semibold mt-6 mb-4">Settings</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Currency"
              value={formData.settings.currency}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  settings: { ...formData.settings, currency: e.target.value },
                })
              }
            />
            <Input
              label="Tax Rate (%)"
              type="number"
              step="0.01"
              value={formData.settings.taxRate}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  settings: { ...formData.settings, taxRate: parseFloat(e.target.value) },
                })
              }
            />
            <Input
              label="Receipt Prefix"
              value={formData.settings.receiptPrefix}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  settings: { ...formData.settings, receiptPrefix: e.target.value },
                })
              }
            />
          </div>

          <div className="flex space-x-2 mt-6">
            <Button type="submit" className="flex-1">
              {editMode ? 'Update' : 'Create'} Branch
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowModal(false);
                resetForm();
              }}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Branches;
